import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertTriangle, XCircle, Info } from "lucide-react";
import { ResultLevel } from "@/components/AnalysisResults";

interface DetailedAnalysisResultsProps {
  results: {
    type: string;
    level: ResultLevel;
    score: number;
    details: string;
  }[];
  overallStatus: ResultLevel;
  flags: string[];
  modelName: string;
  explanation: string;
  confidence: number;
  timestamp: Date;
}

const getStatusColor = (level: ResultLevel) => {
  switch (level) {
    case "safe":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100";
    case "suspicious":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100";
    case "fraudulent":
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100";
  }
};

const getStatusIcon = (level: ResultLevel) => {
  switch (level) {
    case "safe":
      return <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />;
    case "suspicious":
      return <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />;
    case "fraudulent":
      return <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />;
    default:
      return null;
  }
};

const getProgressColor = (score: number) => {
  if (score < 30) return "bg-green-500";
  if (score < 70) return "bg-yellow-500";
  return "bg-red-500";
};

const DetailedAnalysisResults = ({
  results,
  overallStatus,
  flags = [],
  modelName = "AI Analysis Model",
  explanation = "No detailed explanation available.",
  confidence = 0.8,
  timestamp = new Date(),
}: Partial<DetailedAnalysisResultsProps> & { results: DetailedAnalysisResultsProps["results"], overallStatus: ResultLevel }) => {
  return (
    <Tabs defaultValue="summary" className="w-full">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="summary">Summary</TabsTrigger>
        <TabsTrigger value="detailed">Detailed</TabsTrigger>
        <TabsTrigger value="technical">Technical</TabsTrigger>
      </TabsList>
      
      <TabsContent value="summary" className="space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle>Analysis Summary</CardTitle>
              <Badge className={getStatusColor(overallStatus)}>
                <div className="flex items-center gap-1">
                  {getStatusIcon(overallStatus)}
                  <span className="capitalize">{overallStatus}</span>
                </div>
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {results.map((result, i) => (
                <div key={i} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{result.type}</span>
                    <span className="text-sm text-muted-foreground">{result.score}%</span>
                  </div>
                  <Progress
                    value={result.score}
                    className={`h-2 ${getProgressColor(result.score)}`}
                  />
                  <p className="text-xs text-muted-foreground">{result.details}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="detailed" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Detailed Analysis</CardTitle>
            <CardDescription>Explanation of detection results</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-md bg-primary-foreground/50">
              <p className="text-sm">{explanation}</p>
            </div>
            
            <div>
              <h4 className="font-medium mb-2 text-sm">Detected Patterns</h4>
              <div className="flex flex-wrap gap-2">
                {flags.length > 0 ? (
                  flags.map((flag, i) => (
                    <Badge key={i} variant="outline" className="bg-primary-foreground/30">
                      {flag}
                    </Badge>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No suspicious patterns detected</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="technical" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Technical Details</CardTitle>
            <CardDescription>Information about analysis model and confidence</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium mb-1">Analysis Model</h4>
                <p className="text-sm text-muted-foreground">{modelName}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium mb-1">Analysis Time</h4>
                <p className="text-sm text-muted-foreground">{timestamp.toLocaleString()}</p>
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-medium mb-1">Confidence Score</h4>
              <div className="flex items-center space-x-2">
                <Progress value={confidence * 100} className="h-2" />
                <span className="text-sm text-muted-foreground">{Math.round(confidence * 100)}%</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                <Info className="h-3 w-3 inline mr-1" />
                Confidence indicates how certain the analysis is about its assessment
              </p>
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};

export default DetailedAnalysisResults;
